import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { FetchParams } from "../../../components/controls/table-view";
import { ApplicationError } from "../../../models/errors/application-error";
import { ApplicationSupplierOrderSummaryList } from "../../../models/supplier/application-order-summary-list";
import SupplierOrderService from "../../../services/supplier/supplier-order-service";
import { RootState } from "../../store";

export const fetchSupplierOrders = createAsyncThunk<
    ApplicationSupplierOrderSummaryList | null,
    { supplierId: number; fetchParams: FetchParams },
    { state: RootState }
>("supplierOrders/get", async ({ supplierId, fetchParams }, { dispatch, getState, rejectWithValue }) => {
    const state = getState();
    const supplierOrderService = new SupplierOrderService();

    try {
        let currentPosition: number = state.supplierOrders?.orders?.currentPosition ?? 0;
        const fetchNext: number = 15;
        let count: number = state.supplierOrders?.orders?.count ?? -1;

        if (fetchParams.reset) {
            currentPosition = 0;
            count = -1;
            dispatch(resetSupplierOrders());
        }

        if (currentPosition !== count) {
            return await supplierOrderService.get(
                supplierId,
                currentPosition,
                fetchNext,
                false,
            );
        }

        return null;
    } catch (error: any) {
        const apiError = ApplicationError.handleApiError(error, {});
        return rejectWithValue(apiError);
    }
});

export const generateSupplierOrderPDF = createAsyncThunk<
    Blob,
    { supplierId: number; orderId: number },
    { state: RootState }
>("supplierOrders/pdf", async ({ supplierId, orderId }, { rejectWithValue }) => {
    const supplierOrderService = new SupplierOrderService();

    try {
        return await supplierOrderService.getOrderPDF(supplierId, orderId);
    } catch (error: any) {
        const apiError = ApplicationError.handleApiError(error, {});
        return rejectWithValue(apiError);
    }
});

interface SupplierOrderState {
    orders: ApplicationSupplierOrderSummaryList | null;
    loading: boolean;
}

const initialState: SupplierOrderState = {
    orders: null,
    loading: false,
};

const supplierOrderSlice = createSlice({
    name: "supplierOrders",
    initialState,
    reducers: {
        resetSupplierOrders: (state) => {
            state.orders = initialState.orders;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSupplierOrders.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchSupplierOrders.fulfilled, (state, action: PayloadAction<ApplicationSupplierOrderSummaryList | null>) => {
                state.loading = false;

                if (!action.payload) {
                    return;
                }

                if (state.orders?.orders) {
                    state.orders.orders = state.orders.orders.concat(action.payload.orders);
                    state.orders.currentPosition = action.payload.currentPosition;
                    state.orders.count = action.payload.count;
                    return;
                }

                state.orders = action.payload;
            })
            .addCase(fetchSupplierOrders.rejected, (state) => {
                state.loading = false;
            });
    },
});

export const { resetSupplierOrders } = supplierOrderSlice.actions;
export default supplierOrderSlice.reducer;